
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticProps } from 'next';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import { HappyUsers } from '@/components/landing/HappyUsers';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { HAS_FREE_PLAN } from '@/constants/common';
import WanSvg from '~/svg/wan.svg';
const ACCENT = '#f4b45f';
const SHOWCASE_CDN = 'https://media.easy-peasy.ai/landing/wan-3-0';
const GENERATOR_URL = '/ai-videos?model=WAN%203.0';
const OFFICIAL_URL = 'https://modelstudio.console.alibabacloud.com/model-releases/wan3.0-video';
const CheckIcon = () => (<svg className='h-4 w-4 flex-shrink-0 text-amber-300' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
  </svg>);
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='mb-4 inline-block text-xs font-bold uppercase tracking-[0.22em]' style={{ color: ACCENT }}>
    {children}
  </span>);
type VideoExample = {
    src: string;
    poster: string;
    label: string;
    description: string;
    portrait?: boolean;
};
/**
 * Official WAN 3.0 launch outputs from Alibaba Cloud Model Studio, published
 * 2026-08-06. The masters were downloaded from Alibaba's own
 * cloud.video.taobao.com URLs, resized to a 1280px maximum edge, encoded as
 * H.264 CRF 28 + AAC 96k with faststart, and re-hosted in the R2 `images`
 * bucket under `landing/wan-3-0/`. Native stereo audio is preserved.
 */
const videoExamples: VideoExample[] = [
    {
        src: `${SHOWCASE_CDN}/wan-character-sparring.mp4`,
        poster: `${SHOWCASE_CDN}/wan-character-sparring-poster.jpg`,
        label: 'Two-Character Action',
        description: 'Two reference characters stay distinct through a fast 30-second sparring sequence',
    },
    {
        src: `${SHOWCASE_CDN}/wan-everything-ui.mp4`,
        poster: `${SHOWCASE_CDN}/wan-everything-ui-poster.jpg`,
        label: 'Typography & Interface Motion',
        description: 'Editorial grids, product details, and readable brand language share one visual system',
    },
    {
        src: `${SHOWCASE_CDN}/wan-jdrama-reunion.mp4`,
        poster: `${SHOWCASE_CDN}/wan-jdrama-reunion-poster.jpg`,
        label: 'Long-Form Character Story',
        description: 'A 30-second reunion moves across time while identity, wardrobe, and narration stay coherent',
    },
    {
        src: `${SHOWCASE_CDN}/wan-one-take-backstage.mp4`,
        poster: `${SHOWCASE_CDN}/wan-one-take-backstage-poster.jpg`,
        label: 'Continuous Backstage Take',
        description: 'Performance, camera blocking, and native sound build toward the stage in one unbroken move',
    },
    {
        src: `${SHOWCASE_CDN}/wan-vertical-trailer.mp4`,
        poster: `${SHOWCASE_CDN}/wan-vertical-trailer-poster.jpg`,
        label: 'Vertical Film Trailer',
        description: 'Reference-led characters and cinematic pacing composed specifically for a portrait frame',
        portrait: true,
    },
    {
        src: `${SHOWCASE_CDN}/wan-premium-ugc.mp4`,
        poster: `${SHOWCASE_CDN}/wan-premium-ugc-poster.jpg`,
        label: 'Product-Led UGC',
        description: 'The same presenter, product, and kitchen hold together through a complete vertical ad',
        portrait: true,
    },
];
const VideoShowcase = ({ items, t, }: {
    items: VideoExample[];
    t: (key: string) => string;
}) => {
    const [failed, setFailed] = useState<string[]>([]);
    const visible = items.filter((item) => !failed.includes(item.src));
    if (visible.length === 0)
        return null;
    return (<section id='examples' className='border-t border-white/[0.05] py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto mb-16 max-w-2xl text-center'>
          <SectionLabel>{t('Official Video Examples')}</SectionLabel>
          <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
            {t('See WAN 3.0 Tell the Whole Story')}
          </h2>
          <p className='mt-6 text-lg leading-relaxed text-stone-400'>
            {t('Every clip below comes from Alibaba Cloud’s official WAN 3.0 launch. Press play with sound to hear the model’s native audio-visual generation.')}
          </p>
          <a href={OFFICIAL_URL} target='_blank' rel='noreferrer' className='mt-5 inline-flex text-sm font-medium text-amber-300 underline decoration-amber-300/30 underline-offset-4 transition-colors hover:text-amber-200'>
            {t('View the official WAN 3.0 release ↗')}
          </a>
        </div>

        <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {visible.map((video) => (<article key={video.src} className='group'>
              <div className='relative overflow-hidden rounded-2xl border border-white/[0.08] bg-stone-950'>
                <video src={video.src} poster={video.poster} controls playsInline preload='none' className={`aspect-video w-full ${video.portrait ? 'object-contain' : 'object-cover'}`} onError={() => setFailed((current) => current.includes(video.src)
                ? current
                : [...current, video.src])}>
                  {t('Your browser does not support HTML video.')}
                </video>
              </div>
              <div className='mt-3 px-1'>
                <h3 className='text-sm font-semibold text-stone-100'>
                  {t(video.label)}
                </h3>
                <p className='mt-1 text-xs leading-relaxed text-stone-500'>
                  {t(video.description)}
                </p>
              </div>
            </article>))}
        </div>
      </div>
    </section>);
};
export default function Wan30LandingPage() {
    const { t } = useLocale('wan-3-0');
    const creationModes = [
        {
            tag: 'T2V',
            title: t('Text to Video'),
            description: t('Write the scene, performances, camera, dialogue, and score. WAN 3.0 turns one director prompt into a complete take with sound already synchronized.'),
            points: [
                t('5–30 second output on Easy-Peasy.AI'),
                t('Prompts up to 20,000 characters'),
                t('Five landscape, square, and vertical ratios'),
            ],
        },
        {
            tag: 'I2V',
            title: t('Image to Video'),
            description: t('Animate a first frame while preserving the subject and composition, or add a last frame to give the shot an exact visual destination.'),
            points: [
                t('First-frame animation from your image'),
                t('Optional last frame for controlled transitions'),
                t('480P, 720P, or 1080P output'),
            ],
        },
        {
            tag: 'R2V',
            title: t('Reference to Video'),
            description: t('Combine character, product, location, and style images with reference audio. Tell WAN what each source contributes in natural language.'),
            points: [
                t('Up to 9 reference images on Easy-Peasy.AI'),
                t('Up to 3 reference audio clips, 30 seconds total'),
                t('Character, object, style, voice, and music direction'),
            ],
        },
    ];
    const storyBeats = [
        { time: '0–7s', label: t('Establish the world'), width: '23%' },
        { time: '7–15s', label: t('Build the action'), width: '27%' },
        { time: '15–23s', label: t('Turn the story'), width: '27%' },
        { time: '23–30s', label: t('Land the ending'), width: '23%' },
    ];
    const workflow = [
        {
            step: '01',
            title: t('Choose your starting point'),
            description: t('Select WAN 3.0 for text, WAN 3.0 Image for a first frame, or WAN 3.0 Reference for multimodal direction.'),
        },
        {
            step: '02',
            title: t('Load the visual rules'),
            description: t('Add the people, products, locations, and style references that should remain recognizable throughout the take.'),
        },
        {
            step: '03',
            title: t('Direct picture and sound together'),
            description: t('Describe action, framing, dialogue, ambience, music, and timing in the same prompt so they share one timeline.'),
        },
        {
            step: '04',
            title: t('Set the finish and render'),
            description: t('Choose duration, aspect ratio, and resolution. Keep native audio on, or switch it off for a silent production plate.'),
        },
    ];
    const specs = [
        {
            label: t('Maximum duration'),
            value: '30s',
            detail: t('Eight practical duration presets'),
        },
        {
            label: t('Resolution'),
            value: '1080P',
            detail: t('480P, 720P, and 1080P tiers'),
        },
        {
            label: t('Frame rate'),
            value: '30fps',
            detail: t('Official WAN 3.0 output rate'),
        },
        {
            label: t('Native audio'),
            value: t('Optional'),
            detail: t('Dialogue, music, effects, and ambience'),
        },
        {
            label: t('Aspect ratios'),
            value: '5',
            detail: '16:9 · 9:16 · 1:1 · 4:3 · 3:4',
        },
        {
            label: t('Prompt length'),
            value: '20K',
            detail: t('Characters in the Easy-Peasy generator'),
        },
        {
            label: t('Reference images'),
            value: '9',
            detail: t('Character, object, and style roles'),
        },
        {
            label: t('Reference audio'),
            value: '3',
            detail: t('WAV or MP3, 30 seconds combined'),
        },
        {
            label: t('First & last frame'),
            value: t('Yes'),
            detail: t('Controlled opening and destination'),
        },
    ];
    const prices = [
        {
            resolution: '480P',
            credits: '2.5',
            description: t('Fast drafts and storyboard exploration'),
        },
        {
            resolution: '720P',
            credits: '5',
            description: t('Balanced quality for everyday production'),
            featured: true,
        },
        {
            resolution: '1080P',
            credits: '10',
            description: t('Maximum detail for final delivery'),
        },
    ];
    const useCases = [
        {
            number: '01',
            title: t('Narrative & Pre-visualization'),
            description: t('Block a full story beat, trailer passage, or complex camera move before committing a crew and location.'),
        },
        {
            number: '02',
            title: t('Brand Films & Product Launches'),
            description: t('Hold products and visual identity steady while performance, typography, music, and camera work build the campaign.'),
        },
        {
            number: '03',
            title: t('Vertical Ads & UGC'),
            description: t('Create portrait-first presenter spots with consistent products, spoken lines, close-ups, and a finished soundtrack.'),
        },
        {
            number: '04',
            title: t('Music & Performance'),
            description: t('Direct choreography, vocal performance, camera rhythm, ambience, and score as one synchronized piece.'),
        },
    ];
    const faqs = [
        {
            question: t('What is WAN 3.0?'),
            answer: t('WAN 3.0 is Alibaba’s all-in-one video generation model, released through Alibaba Cloud Model Studio in August 2026. It creates up to 30 seconds of 30fps video from text, first and last frames, or multimodal references, with audio generated alongside the picture.'),
        },
        {
            question: t('How long can a WAN 3.0 video be?'),
            answer: t('Easy-Peasy.AI offers 5, 8, 10, 12, 15, 20, 25, and 30-second outputs. The 30-second ceiling applies to text, image, and reference workflows at every available resolution.'),
        },
        {
            question: t('Does WAN 3.0 generate audio?'),
            answer: t('Yes. WAN 3.0 can generate dialogue, music, sound effects, and ambience in the same pass as the video. The Easy-Peasy.AI generator also lets you turn native audio off when you need a silent plate.'),
        },
        {
            question: t('What references can I use on Easy-Peasy.AI?'),
            answer: t('WAN 3.0 Reference accepts up to 9 reference images and 3 WAV or MP3 audio clips on Easy-Peasy.AI. The audio clips can total up to 30 seconds and must be paired with at least one image reference.'),
        },
        {
            question: t('Can WAN 3.0 use first and last frames?'),
            answer: t('Yes. Start with one image and optionally add a second image as the final frame. Describe the movement between them rather than repeating what the last frame already shows.'),
        },
        {
            question: t('How much does WAN 3.0 cost on Easy-Peasy.AI?'),
            answer: t('Pricing is based on generated duration and resolution: 2.5 credits per second at 480P, 5 credits per second at 720P, and 10 credits per second at 1080P. The default 5-second 720P render is 25 credits.'),
        },
        {
            question: t('Can I use WAN 3.0 videos commercially?'),
            answer: t('Yes. You can use outputs for ads, social content, client work, product pages, games, and other commercial projects, subject to the Easy-Peasy.AI Terms of Use and your rights to every uploaded reference.'),
        },
    ];
    return (<main className='relative overflow-hidden bg-[#0c0a08] text-stone-100'>
      <Seo templateTitle={t('WAN 3.0 AI Video Generator | 30 Seconds, 1080P, Native Audio')} description={t('Create cinematic WAN 3.0 videos from text, images, and audio references. Generate up to 30 seconds at 1080P with native dialogue, music, and effects.')} image={`${SHOWCASE_CDN}/wan-one-take-backstage-poster.jpg`}/>

      <section className='relative min-h-[760px] overflow-hidden sm:min-h-[94vh]'>
        <video autoPlay muted loop playsInline preload='metadata' poster={`${SHOWCASE_CDN}/wan-one-take-backstage-poster.jpg`} src={`${SHOWCASE_CDN}/wan-one-take-backstage.mp4`} className='absolute inset-0 h-full w-full object-cover'/>
        <div className='absolute inset-0 bg-[#0c0a08]/70'/>
        <div className='absolute inset-0 bg-gradient-to-b from-[#0c0a08]/30 via-transparent to-[#0c0a08]'/>
        <div className='absolute inset-y-0 left-[8%] w-px bg-white/[0.08]'/>
        <div className='absolute inset-y-0 right-[8%] w-px bg-white/[0.08]'/>

        <div className='absolute left-0 top-0 z-30 w-full'>
          <LandingPageNavigation variant='light' forceDark/>
        </div>

        <div className='relative z-20 mx-auto flex min-h-[760px] max-w-7xl items-center px-6 pb-20 pt-40 sm:min-h-[94vh] lg:px-8'>
          <div className='max-w-4xl'>
            <div className='mb-9 inline-flex items-center gap-3 rounded-full border border-white/15 bg-black/30 px-4 py-2 text-xs font-medium tracking-wide text-stone-200 backdrop-blur-md'>
              <span className='flex h-8 w-8 items-center justify-center rounded-full bg-[#26211d] ring-1 ring-white/15'>
                <WanSvg className='h-5 w-5' aria-hidden='true'/>
              </span>
              {t("Alibaba's all-in-one video model")}
            </div>

            <h1 className='max-w-4xl text-6xl font-black leading-[0.88] tracking-[-0.05em] text-stone-50 sm:text-8xl lg:text-[7.5rem]'>
              <span className='block'>WAN 3.0</span>
              <span className='mt-4 block text-3xl font-semibold leading-tight tracking-[-0.03em] text-amber-300 sm:text-5xl lg:text-6xl'>
                {t('One take. A whole story.')}
              </span>
            </h1>

            <p className='mt-8 max-w-2xl text-lg leading-relaxed text-stone-300 sm:text-xl'>
              {t('Direct up to 30 seconds of cinematic video from text, first and last frames, or multimodal references—with dialogue, music, and effects generated in the same pass.')}
            </p>

            <div className='mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center'>
              <Link href={GENERATOR_URL} className='group inline-flex items-center justify-center rounded-full bg-amber-300 px-8 py-4 text-base font-bold text-[#21140b] shadow-[0_18px_55px_-20px_rgba(244,180,95,0.9)] transition-transform hover:scale-[1.025]'>
                {t('Create with WAN 3.0')}
                <span className='ml-2 transition-transform group-hover:translate-x-1'>
                  →
                </span>
              </Link>
              <a href='#examples' className='px-3 py-3 text-sm font-semibold text-stone-200 transition-colors hover:text-amber-200'>
                {t('Watch official examples')} ↓
              </a>
            </div>

            <div className='mt-8'>
              <HappyUsers fromLandingPage={true} className='items-start'/>
            </div>

            <div className='mt-14 flex flex-wrap gap-x-8 gap-y-4 border-t border-white/15 pt-6'>
              {[
            ['30s', t('One generation')],
            ['1080P', t('Maximum output')],
            ['A/V', t('Native sound')],
            ['20K', t('Prompt characters')],
        ].map(([value, label]) => (<div key={label} className='min-w-[105px]'>
                  <div className='text-2xl font-bold text-amber-200'>
                    {value}
                  </div>
                  <div className='mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500'>
                    {label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </section>

      <LogosCloud />

      <section className='border-y border-amber-200/10 bg-amber-200/[0.035] py-24 sm:py-28'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end'>
            <div>
              <SectionLabel>{t('Native 30-Second Storytelling')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
                {t('Give the Idea Room to Become a Story')}
              </h2>
            </div>
            <p className='max-w-2xl text-lg leading-relaxed text-stone-400'>
              {t('A single WAN 3.0 generation can carry setup, movement, performance, dialogue, and resolution. Use time ranges in the prompt to pace the take instead of squeezing the whole idea into one instant.')}
            </p>
          </div>

          <div className='mt-14 overflow-hidden rounded-2xl border border-white/[0.08] bg-black/25 p-5 sm:p-7'>
            <div className='flex h-3 overflow-hidden rounded-full bg-white/[0.06]'>
              {storyBeats.map((beat, index) => (<div key={beat.time} style={{ width: beat.width }} className={index % 2 === 0 ? 'bg-amber-300' : 'bg-orange-500'}/>))}
            </div>
            <div className='mt-6 grid grid-cols-2 gap-5 lg:grid-cols-4'>
              {storyBeats.map((beat) => (<div key={beat.time}>
                  <div className='text-xs font-bold text-amber-300'>
                    {beat.time}
                  </div>
                  <div className='mt-1 text-sm text-stone-300'>
                    {beat.label}
                  </div>
                </div>))}
            </div>
          </div>
        </div>
      </section>

      <section className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Three Ways to Create')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('Start with an Idea, a Frame, or a Full Cast')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-stone-400'>
              {t('The same WAN 3.0 family covers text, image, and reference-led generation, so you can choose the amount of control each shot needs.')}
            </p>
          </div>

          <div className='grid gap-5 lg:grid-cols-3'>
            {creationModes.map((mode, index) => (<article key={mode.tag} className={`relative overflow-hidden rounded-2xl border p-8 ${index === 1
                ? 'border-amber-300/25 bg-amber-300/[0.045]'
                : 'border-white/[0.08] bg-white/[0.025]'}`}>
                <span className='text-xs font-black tracking-[0.2em] text-amber-300'>
                  {mode.tag}
                </span>
                <h3 className='mt-5 text-2xl font-bold text-stone-100'>
                  {mode.title}
                </h3>
                <p className='mt-4 text-sm leading-relaxed text-stone-400'>
                  {mode.description}
                </p>
                <ul className='mt-7 space-y-3'>
                  {mode.points.map((point) => (<li key={point} className='flex items-start gap-2.5 text-sm text-stone-400'>
                      <CheckIcon />
                      <span>{point}</span>
                    </li>))}
                </ul>
              </article>))}
          </div>
        </div>
      </section>

      <VideoShowcase items={videoExamples} t={t}/>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2 lg:items-center lg:px-8'>
          <div>
            <SectionLabel>{t('Multimodal Direction')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('Keep Every Character, Product, and Sound on Brief')}
            </h2>
            <p className='mt-6 text-lg leading-relaxed text-stone-400'>
              {t('Reference mode gives the prompt a concrete cast and visual world. Assign each file a role—who appears, what they hold, where the scene happens, and which voice or music guides the performance.')}
            </p>
            <div className='mt-8 rounded-2xl border border-amber-300/20 bg-amber-300/[0.045] p-6'>
              <div className='text-[10px] font-black uppercase tracking-[0.2em] text-amber-300'>
                {t('Example direction')}
              </div>
              <p className='mt-3 text-sm italic leading-relaxed text-stone-300'>
                &ldquo;
                {t('Image 1 is the presenter. Keep the bottle from Image 2 unchanged. Use Image 3 for the kitchen. Match the speaking tone to Audio 1 and begin the music from Audio 2 after 12 seconds.')}
                &rdquo;
              </p>
            </div>
          </div>

          <div className='relative min-h-[430px]'>
            <div className='absolute left-0 top-0 w-[82%] rounded-2xl border border-white/[0.09] bg-[#17120e] p-6'>
              <div className='text-4xl font-black text-amber-300'>9</div>
              <h3 className='mt-2 font-bold text-stone-100'>
                {t('Reference images')}
              </h3>
              <p className='mt-2 text-sm leading-relaxed text-stone-500'>
                {t('Characters, products, locations, composition, and style')}
              </p>
            </div>
            <div className='absolute right-0 top-36 w-[76%] rounded-2xl border border-orange-400/20 bg-[#1a110c] p-6'>
              <div className='text-4xl font-black text-orange-400'>3</div>
              <h3 className='mt-2 font-bold text-stone-100'>
                {t('Reference audio clips')}
              </h3>
              <p className='mt-2 text-sm leading-relaxed text-stone-500'>
                {t('Voice, singing, music, and sound direction')}
              </p>
            </div>
            <div className='absolute bottom-0 left-10 w-[84%] rounded-2xl border border-white/[0.09] bg-[#12100e] p-6'>
              <div className='text-4xl font-black text-stone-200'>20K</div>
              <h3 className='mt-2 font-bold text-stone-100'>
                {t('Prompt characters')}
              </h3>
              <p className='mt-2 text-sm leading-relaxed text-stone-500'>
                {t('Enough room for timed beats, dialogue, camera, and sound')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Workflow')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('From Brief to Finished Take')}
            </h2>
          </div>
          <div className='grid gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {workflow.map((item) => (<article key={item.step}>
                <div className='text-5xl font-black text-amber-300/20'>
                  {item.step}
                </div>
                <h3 className='mt-3 text-lg font-bold text-stone-100'>
                  {item.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-stone-400'>
                  {item.description}
                </p>
              </article>))}
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-14 max-w-2xl text-center'>
            <SectionLabel>{t('Specifications')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('WAN 3.0 Technical Details')}
            </h2>
          </div>
          <div className='mx-auto grid max-w-6xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
            {specs.map((spec) => (<div key={spec.label} className='border-t border-white/[0.12] px-1 py-5'>
                <div className='text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-600'>
                  {spec.label}
                </div>
                <div className='mt-2 text-2xl font-bold text-amber-300'>
                  {spec.value}
                </div>
                <div className='mt-1 text-xs leading-relaxed text-stone-500'>
                  {spec.detail}
                </div>
              </div>))}
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:items-start'>
            <div>
              <SectionLabel>{t('Simple Per-Second Pricing')}</SectionLabel>
              <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
                {t('Draft Fast. Finish in 1080P.')}
              </h2>
              <p className='mt-6 text-lg leading-relaxed text-stone-400'>
                {t('Duration and resolution are the only price levers. Reference images, reference audio, and native sound do not add a separate Easy-Peasy.AI fee.')}
              </p>
            </div>
            <div className='grid gap-4 sm:grid-cols-3'>
              {prices.map((price) => (<article key={price.resolution} className={`rounded-2xl border p-6 ${price.featured
                ? 'border-amber-300/40 bg-amber-300/[0.07]'
                : 'border-white/[0.08] bg-white/[0.025]'}`}>
                  <div className='text-xs font-bold tracking-[0.18em] text-stone-500'>
                    {price.resolution}
                  </div>
                  <div className='mt-4 text-4xl font-black text-stone-100'>
                    {price.credits}
                  </div>
                  <div className='text-xs text-amber-300'>
                    {t('credits / second')}
                  </div>
                  <p className='mt-5 text-sm leading-relaxed text-stone-500'>
                    {price.description}
                  </p>
                </article>))}
            </div>
          </div>
        </div>
      </section>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-16 max-w-2xl text-center'>
            <SectionLabel>{t('Production Use Cases')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('What You Can Make with WAN 3.0')}
            </h2>
          </div>
          <div className='grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.08] sm:grid-cols-2'>
            {useCases.map((useCase) => (<article key={useCase.number} className='bg-[#0c0a08] p-8'>
                <div className='text-xs font-black tracking-[0.2em] text-amber-300'>
                  {useCase.number}
                </div>
                <h3 className='mt-4 text-xl font-bold text-stone-100'>
                  {useCase.title}
                </h3>
                <p className='mt-3 text-sm leading-relaxed text-stone-400'>
                  {useCase.description}
                </p>
              </article>))}
          </div>
          <div className='mt-14 flex justify-center'>
            <ButtonLink href={HAS_FREE_PLAN ? GENERATOR_URL : '/#pricing'} variant='dark' className='px-8 py-3 text-lg'>
              {t('Create with WAN 3.0')}
            </ButtonLink>
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      <section className='border-t border-white/[0.05] py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto mb-14 max-w-2xl text-center'>
            <SectionLabel>{t('FAQ')}</SectionLabel>
            <h2 className='text-3xl font-bold tracking-tight text-stone-50 sm:text-5xl'>
              {t('Frequently Asked Questions')}
            </h2>
            <p className='mt-4 text-lg text-stone-400'>
              {t('Everything you need to know about WAN 3.0')}
            </p>
          </div>
          <div className='mx-auto max-w-3xl space-y-4'>
            {faqs.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.08] bg-white/[0.025]'>
                <summary className='flex cursor-pointer items-center justify-between p-5 font-semibold text-stone-100'>
                  {faq.question}
                  <span className='ml-4 text-amber-300 transition-transform group-open:rotate-45'>
                    +
                  </span>
                </summary>
                <div className='px-5 pb-5'>
                  <p className='text-sm leading-relaxed text-stone-400'>
                    {faq.answer}
                  </p>
                </div>
              </details>))}
          </div>
        </div>
      </section>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction preset='ai-videos'/>
        <Footer />
      </div>
    </main>);
}
const getStaticProps: GetStaticProps = async () => ({
    props: {},
});

    async function __Next_Translate__getStaticProps__1a13a29a0e6__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/wan-3-0',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a13a29a0e6__ as getStaticProps }
  